import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Home, Dog, Baby, MessageSquare, Shield, AlertTriangle,
  TrendingUp, Clock, CheckCircle, XCircle, Eye, ArrowRight,
  BarChart3, Activity, Loader2, Sparkles, Mail, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PanelLayout from '@/components/PanelLayout';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

// Metric Card Component
function MetricCard({ label, value, icon: Icon, trend, color = "orange", subtext }) {
  const colors = {
    orange: "bg-[#F47A3B]/10 text-[#F47A3B]",
    green: "bg-green-500/10 text-green-500",
    blue: "bg-blue-500/10 text-blue-500",
    red: "bg-red-500/10 text-red-500",
    yellow: "bg-yellow-500/10 text-yellow-500"
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-600' : 'bg-zinc-100 text-zinc-500'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-zinc-900 mb-1">{value}</p>
      <p className="text-zinc-500 text-sm">{label}</p>
      {subtext && <p className="text-zinc-400 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

// Simple Chart Component
function SimpleChart({ data, label, color = "#F47A3B" }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-5">
      <h3 className="font-semibold text-zinc-900 mb-4">{label}</h3>
      <div className="flex items-end gap-2 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full rounded-t-lg transition-all hover:opacity-80"
              style={{ 
                height: `${(d.count / maxValue) * 100}%`,
                minHeight: d.count > 0 ? '8px' : '2px',
                backgroundColor: d.count > 0 ? color : '#e5e7eb'
              }}
              title={`${d.count}`}
            />
            <span className="text-xs text-zinc-400">
              {new Date(d.date).toLocaleDateString('pl-PL', { weekday: 'short' })}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-right">
        <span className="text-sm text-zinc-500">
          Suma: <span className="font-semibold text-zinc-900">{data.reduce((a, b) => a + b.count, 0)}</span>
        </span>
      </div>
    </div>
  );
}

function CreatorStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [rx, setRx] = useState({ state: 'idle', data: null });

  useEffect(() => { load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const load = async (days = range) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const { data } = await axios.get(`${API}/admin/creator-stats?days=${days}`, { headers });
      setStats(data);
    } catch (e) { console.error('creator-stats', e); }
    finally { setLoading(false); }
  };

  const changeRange = (d) => { setRange(d); load(d); };

  const doReactivation = async (dry) => {
    setRx({ state: dry ? 'previewing' : 'sending', data: null });
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const { data } = await axios.post(`${API}/admin/creator-reactivate?dry_run=${dry}&min_age_hours=1`, {}, { headers });
      setRx({ state: dry ? 'preview' : 'done', data });
      if (!dry) { toast.success(`Wysłano ${data.sent} e-maili reaktywacyjnych`); load(); }
    } catch (e) {
      toast.error('Nie udało się wykonać reaktywacji');
      setRx({ state: 'idle', data: null });
    }
  };

  const pg = stats?.pages_generated || {};
  const ent = stats?.entities_created || {};

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="admin-creator-stats">
      <div className="flex items-center justify-between mb-5">
        <span className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#F47A3B]" /> Kreator stron (/stworz)
        </span>
        <div className="inline-flex rounded-lg border border-zinc-200 p-0.5 bg-zinc-50" data-testid="creator-range-toggle">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => changeRange(d)}
              data-testid={`creator-range-${d}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${range === d ? 'bg-[#F47A3B] text-white' : 'text-zinc-500 hover:text-zinc-800'}`}
            >
              {d} dni
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Ładowanie…</div>
      ) : !stats ? (
        <p className="text-sm text-zinc-500">Brak danych.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-orange-50 border border-orange-100" data-testid="creator-tile-used">
              <p className="text-3xl font-bold text-zinc-900">{stats.range_generated ?? 0}</p>
              <p className="text-sm text-zinc-600">Osób skorzystało ({range} dni)</p>
              <p className="text-xs text-zinc-400 mt-1">łącznie: {pg.soft_signup_total ?? 0}</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-100">
              <p className="text-3xl font-bold text-zinc-900">{stats.range_kennels ?? 0}</p>
              <p className="text-sm text-zinc-500">Hodowle ({range} dni)</p>
              <p className="text-xs text-zinc-400 mt-1">łącznie: {ent.kennels_platform_total ?? 0}</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-100">
              <p className="text-3xl font-bold text-zinc-900">{stats.range_businesses ?? 0}</p>
              <p className="text-sm text-zinc-500">Firmy ({range} dni)</p>
              <p className="text-xs text-zinc-400 mt-1">łącznie: {ent.businesses_platform_total ?? 0}</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-50 border border-zinc-100">
              <p className="text-3xl font-bold text-zinc-900">{pg.conversion_pct ?? 0}%</p>
              <p className="text-sm text-zinc-500">Konwersja na konto</p>
              <p className="text-xs text-zinc-400 mt-1">{pg.converted_to_full_account ?? 0} z {pg.soft_signup_total ?? 0}</p>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-zinc-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-zinc-900 flex items-center gap-2"><Mail className="w-4 h-4 text-[#F47A3B]" /> Reaktywacja porzuconych stron</p>
                <p className="text-sm text-zinc-500">E-mail „dokończ swoją stronę" do kont bez hasła. Scheduler działa też automatycznie.</p>
              </div>
              {rx.state === 'idle' && (
                <Button onClick={() => doReactivation(true)} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white" data-testid="admin-reactivate-preview">
                  Podgląd reaktywacji
                </Button>
              )}
              {rx.state === 'previewing' && <Loader2 className="w-5 h-5 animate-spin text-[#F47A3B]" />}
            </div>

            {rx.state === 'preview' && rx.data && (
              <div className="mt-3 p-4 rounded-xl bg-orange-50 border border-orange-200" data-testid="admin-reactivate-preview-box">
                <p className="text-sm text-zinc-700 mb-3">
                  Do wysłania: <span className="font-bold">{rx.data.total_candidates}</span> {rx.data.total_candidates === 1 ? 'konto' : 'kont'} (podgląd — nic nie wysłano).
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => doReactivation(false)}
                    disabled={!rx.data.total_candidates}
                    className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                    data-testid="admin-reactivate-send"
                  >
                    <Send className="w-4 h-4 mr-2" /> Wyślij teraz ({rx.data.total_candidates})
                  </Button>
                  <Button variant="outline" onClick={() => setRx({ state: 'idle', data: null })} data-testid="admin-reactivate-cancel">Anuluj</Button>
                </div>
              </div>
            )}

            {rx.state === 'sending' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-zinc-600"><Loader2 className="w-4 h-4 animate-spin" /> Wysyłanie…</div>
            )}

            {rx.state === 'done' && rx.data && (
              <div className="mt-3 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800" data-testid="admin-reactivate-result">
                Wysłano {rx.data.sent} e-maili (nieudane: {rx.data.failed}).
              </div>
            )}
          </div>

          {Array.isArray(stats.daily_generated) && stats.daily_generated.length > 0 && (() => {
            const data = stats.daily_generated;
            const H = 100;
            const dense = data.length > 45;
            const showDay = data.length <= 31;
            const max = Math.max(...data.map((d) => (d.kennels || 0) + (d.businesses || 0)), 1);
            const sumK = data.reduce((a, b) => a + (b.kennels || 0), 0);
            const sumB = data.reduce((a, b) => a + (b.businesses || 0), 0);
            return (
              <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="admin-creator-chart">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-sm font-semibold text-zinc-900">Wygenerowane strony ({range} dni)</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#F47A3B' }} /> Hodowle: <b className="text-zinc-700">{sumK}</b></span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#3B82F6' }} /> Firmy: <b className="text-zinc-700">{sumB}</b></span>
                  </div>
                </div>
                <div className={`flex items-end ${dense ? 'gap-px' : 'gap-1.5'}`}>
                  {data.map((d, i) => {
                    const k = d.kennels || 0;
                    const b = d.businesses || 0;
                    const tot = k + b;
                    const kPx = Math.round((k / max) * H);
                    const bPx = Math.round((b / max) * H);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${d.date}: hodowle ${k}, firmy ${b}`}>
                        {!dense && <span className="text-[10px] text-zinc-400 h-3">{tot > 0 ? tot : ''}</span>}
                        <div className="w-full flex flex-col justify-end" style={{ height: `${H}px` }}>
                          {k > 0 && <div className="w-full rounded-t-md transition-all hover:opacity-80" style={{ height: `${Math.max(kPx, 3)}px`, backgroundColor: '#F47A3B' }} />}
                          {b > 0 && <div className={`w-full transition-all hover:opacity-80 ${k === 0 ? 'rounded-t-md' : ''}`} style={{ height: `${Math.max(bPx, 3)}px`, backgroundColor: '#3B82F6' }} />}
                          {tot === 0 && <div className="w-full rounded-t-md" style={{ height: '2px', backgroundColor: '#e5e7eb' }} />}
                        </div>
                        {showDay && <span className="text-[9px] text-zinc-400">{new Date(d.date).getDate()}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');

  useEffect(() => {
    fetchData();
  }, [range]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [metricsRes, chartsRes] = await Promise.all([
        axios.get(`${API}/admin/metrics?range=${range}`, { headers }),
        axios.get(`${API}/admin/metrics/charts`, { headers })
      ]);
      
      setMetrics(metricsRes.data);
      setCharts(chartsRes.data);
    } catch (e) {
      console.error('Error fetching admin data:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Admin Dashboard</h1>
            <p className="text-zinc-500">Monitoruj wzrost i jakość platformy</p>
          </div>
          
          <div className="flex items-center gap-2">
            {['24h', '7d', '30d'].map(r => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  range === r 
                    ? 'bg-[#F47A3B] text-white' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                {r === '24h' ? '24h' : r === '7d' ? '7 dni' : '30 dni'}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/admin/moderation?status=pending">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 hover:bg-yellow-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span className="font-semibold text-yellow-800">
                  {metrics?.moderation?.pending_total || 0}
                </span>
              </div>
              <p className="text-sm text-yellow-700">Do weryfikacji</p>
            </div>
          </Link>
          
          <Link to="/admin/moderation?flagged=true">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 hover:bg-red-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-800">
                  {metrics?.moderation?.flagged_total || 0}
                </span>
              </div>
              <p className="text-sm text-red-700">Podejrzane</p>
            </div>
          </Link>
          
          <Link to="/admin/users">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 hover:bg-blue-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-blue-800">
                  {metrics?.users?.total || 0}
                </span>
              </div>
              <p className="text-sm text-blue-700">Użytkownicy</p>
            </div>
          </Link>
          
          <Link to="/admin/moderation?status=published">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 hover:bg-green-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800">
                  {metrics?.totals?.kennels + metrics?.totals?.studs + metrics?.totals?.litters || 0}
                </span>
              </div>
              <p className="text-sm text-green-700">Opublikowane</p>
            </div>
          </Link>
        </div>

        {/* Main Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard 
            label="Nowe konta"
            value={metrics?.period?.new_users || 0}
            icon={Users}
            color="blue"
            subtext={`Łącznie: ${metrics?.totals?.users || 0}`}
          />
          <MetricCard 
            label="Nowe strony"
            value={metrics?.period?.total_new_pages || 0}
            icon={Home}
            color="green"
            subtext={`H: ${metrics?.period?.new_kennels || 0} | R: ${metrics?.period?.new_studs || 0} | M: ${metrics?.period?.new_litters || 0}`}
          />
          <MetricCard 
            label="Nowe zapytania"
            value={metrics?.period?.new_leads || 0}
            icon={MessageSquare}
            color="orange"
            subtext={`Łącznie: ${metrics?.totals?.leads || 0}`}
          />
          <MetricCard 
            label="Zablokowani"
            value={metrics?.users?.blocked || 0}
            icon={Shield}
            color="red"
            subtext={`Aktywni: ${metrics?.users?.active || 0}`}
          />
        </div>

        {/* Creator stats + reaktywacja */}
        <CreatorStatsCard />

        {/* Moderation Stats */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#F47A3B]" />
            Status moderacji
          </h2>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-xl">
              <p className="text-3xl font-bold text-yellow-600">{metrics?.moderation?.pending_kennels || 0}</p>
              <p className="text-sm text-yellow-700">Hodowle pending</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-xl">
              <p className="text-3xl font-bold text-yellow-600">{metrics?.moderation?.pending_studs || 0}</p>
              <p className="text-sm text-yellow-700">Reproduktory pending</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-xl">
              <p className="text-3xl font-bold text-yellow-600">{metrics?.moderation?.pending_litters || 0}</p>
              <p className="text-sm text-yellow-700">Mioty pending</p>
            </div>
          </div>
          
          <div className="mt-4 flex justify-center">
            <Link to="/admin/moderation?status=pending">
              <Button className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white">
                Przejdź do kolejki moderacji
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Charts */}
        {charts && (
          <div className="grid md:grid-cols-3 gap-6">
            <SimpleChart 
              data={charts.registrations} 
              label="Rejestracje (7 dni)" 
              color="#3B82F6"
            />
            <SimpleChart 
              data={charts.pages} 
              label="Nowe strony (7 dni)" 
              color="#10B981"
            />
            <SimpleChart 
              data={charts.leads} 
              label="Zapytania (7 dni)" 
              color="#F47A3B"
            />
          </div>
        )}

        {/* Quick Links */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/moderation" className="block">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-lg transition-shadow group">
              <Eye className="w-8 h-8 text-[#F47A3B] mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-zinc-900">Kolejka moderacji</h3>
              <p className="text-sm text-zinc-500">Weryfikuj i publikuj strony</p>
            </div>
          </Link>
          
          <Link to="/admin/users" className="block">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-lg transition-shadow group">
              <Users className="w-8 h-8 text-blue-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-zinc-900">Użytkownicy</h3>
              <p className="text-sm text-zinc-500">Zarządzaj kontami</p>
            </div>
          </Link>
          
          <Link to="/admin/kennels" className="block">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-lg transition-shadow group">
              <Home className="w-8 h-8 text-green-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-zinc-900">Hodowle</h3>
              <p className="text-sm text-zinc-500">Przeglądaj wszystkie</p>
            </div>
          </Link>
          
          <Link to="/admin/stats" className="block">
            <div className="bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-lg transition-shadow group">
              <BarChart3 className="w-8 h-8 text-purple-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-zinc-900">Analityka</h3>
              <p className="text-sm text-zinc-500">Szczegółowe statystyki</p>
            </div>
          </Link>
        </div>
      </div>
    </PanelLayout>
  );
}
